import React from "react"

import { TextBig } from "@tenforce/product-fields"
import { ActionAnchor, ActionType } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"

import { ICheckListViewActions, IQuestion } from "../.."
import ContextMenu from "../ContextMenu"

import ActionList from "./ActionList"
import FindingList from "./FindingList"
import QuestionValue from "./questions/QuestionValue"

export interface Props extends IQuestion {
    contextMenuActions: ICheckListViewActions
}

export const Question = (props: Props): JSX.Element => {
    const {
        actions,
        contextMenuActions,
        controls,
        findings,
        id,
        remark,
        title,
        url,
    } = props

    const { labels } = useAppSelector((state: GlobalState) => state.audits)

    const hasContent =
        remark.length > 0 ||
        (!!findings && findings.length > 0) ||
        (!!actions && actions.length > 0)

    return (
        <div
            className="theme-bubble bg-platinum has-hidden-actions tw-p-2"
            data-ft-component="question"
            data-ft-id={id}
            data-ft-title={title}
        >
            <div className="tw-flex">
                <div className="tw-flex tw-flex-col tw-flex-1 tw-pt-2">
                    <div
                        className="h6 tw-px-6 tw-whitespace-pre-line"
                        data-ft-component="title"
                    >
                        {title}
                    </div>
                    <QuestionValue {...props} />
                </div>

                <aside className="tw-content-center">
                    <ActionAnchor
                        actionType={ActionType.icon}
                        extraClasses="is-hidden-action"
                        data-ft-component="goToLink"
                        href={url}
                        title={title}
                    >
                        {Symbols.ArrowForward}
                    </ActionAnchor>

                    <ContextMenu
                        type="Question"
                        resourceId={id}
                        controls={controls}
                        contextMenuActions={contextMenuActions}
                        remark={remark}
                    />
                </aside>
            </div>

            {hasContent && (
                <div className="tw-flex tw-flex-col tw-mt-2">
                    {
                        // if no remark was set, it should be done via the context menu
                        remark.length > 0 && (
                            <div data-ft-component="remarkInput">
                                <TextBig
                                    value={remark}
                                    title={labels.Remark}
                                    placeholder={labels.RemarkPlaceholder}
                                    isReadOnly={!controls.canEditRemark}
                                    fieldId="remarkInput"
                                    isLoading={false}
                                    data-ft-is-editable={controls.canEditRemark}
                                    onSave={(value): void => {
                                        contextMenuActions.updateRemark(
                                            id,
                                            value,
                                        )
                                    }}
                                />
                            </div>
                        )
                    }

                    <FindingList
                        containerElement="questionFindingsContainer"
                        contextMenuActions={contextMenuActions}
                        findings={findings}
                        isLabelShown={true}
                    />

                    <ActionList
                        actions={actions}
                        containerElement="questionActionsContainer"
                        isLabelShown={true}
                    />
                </div>
            )}
        </div>
    )
}

export default Question
